'use client';

import { useState, FormEvent, CSSProperties } from 'react';
import { V4CtaButton } from '@/components/landing/v4-shared';
import { pushEvent, CONVERSION_EVENTS } from '@/lib/analytics/core';

interface FormState {
  email: string;
  name: string;
  business: string;
  industry: string;
}

const INITIAL: FormState = { email: '', name: '', business: '', industry: '' };

const labelStyle: CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-geist-mono)',
  fontSize: 11,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--dim)',
  margin: '0 0 6px',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: 'rgba(244,244,240,0.04)',
  border: '1px solid var(--hair-2)',
  borderRadius: 12,
  color: 'var(--paper)',
  fontFamily: 'var(--font-geist-sans)',
  fontSize: 15,
};

export function DemoLeadForm() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    const email = form.email.trim();
    if (!email) {
      setStatus('error');
      setError('Please enter your work email so we can follow up.');
      return;
    }
    if (!email.includes('@')) {
      setStatus('error');
      setError('Email needs an @ symbol (e.g. you@business.com).');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setError('That email looks incomplete. Try the full address like you@business.com.');
      return;
    }

    setStatus('submitting');
    setError(null);

    const utm = new URLSearchParams(window.location.search);
    const payload = {
      ...form,
      email,
      utm_source: utm.get('utm_source') || undefined,
      utm_medium: utm.get('utm_medium') || undefined,
      utm_campaign: utm.get('utm_campaign') || undefined,
      referrer: document.referrer || undefined,
    };

    try {
      const res = await fetch('/api/demo-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setStatus('error');
        const code = body?.error;
        if (code === 'invalid_email') {
          setError('That email looks incomplete. Try the full address like you@business.com.');
        } else if (code === 'disposable_email_blocked') {
          setError('Please use your work email — disposable/temporary inboxes aren’t accepted.');
        } else if (code === 'rate_limited' || code === 'rate_limited_email') {
          setError('Too many requests. Please wait a few minutes and try again.');
        } else {
          setError('Something went wrong. Please try again in a moment.');
        }
        return;
      }
      setStatus('success');
      pushEvent(CONVERSION_EVENTS.DEMO_BOOKED, {
        industry: form.industry || undefined,
        source: 'demo_page',
      });
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  }

  if (status === 'success') {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <div
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            background: 'rgba(125,250,255,0.18)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--cyan-glow)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 22,
            fontWeight: 500,
            color: 'var(--paper)',
            margin: '0 0 8px',
          }}
        >
          You&rsquo;re on the list.
        </h2>
        <p style={{ color: 'var(--dim)', fontSize: 14, margin: 0 }}>
          We&apos;ll reach out within one business day to schedule your walkthrough.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
      <h2
        style={{
          fontFamily: 'var(--font-geist-sans)',
          fontSize: 18,
          fontWeight: 500,
          color: 'var(--paper)',
          margin: '0 0 4px',
        }}
      >
        Book your walkthrough
      </h2>

      <div>
        <label htmlFor="demo-email" style={labelStyle}>
          Work email <span style={{ color: 'var(--cyan-glow)' }}>*</span>
        </label>
        <input
          id="demo-email"
          type="email"
          required
          autoComplete="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          style={inputStyle}
          placeholder="you@business.com"
        />
      </div>

      <div>
        <label htmlFor="demo-name" style={labelStyle}>Your name</label>
        <input
          id="demo-name"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          style={inputStyle}
          placeholder="Jane Smith"
        />
      </div>

      <div>
        <label htmlFor="demo-business" style={labelStyle}>Business name</label>
        <input
          id="demo-business"
          type="text"
          autoComplete="organization"
          value={form.business}
          onChange={(e) => setForm({ ...form, business: e.target.value })}
          style={inputStyle}
          placeholder="Smith's Plumbing"
        />
      </div>

      <div>
        <label htmlFor="demo-industry" style={labelStyle}>Industry</label>
        <select
          id="demo-industry"
          value={form.industry}
          onChange={(e) => setForm({ ...form, industry: e.target.value })}
          style={inputStyle}
        >
          <option value="">Select industry…</option>
          <option value="home_services">Home Services</option>
          <option value="healthcare">Healthcare / Dental</option>
          <option value="professional">Professional Services</option>
          <option value="real_estate">Real Estate</option>
          <option value="automotive">Automotive</option>
          <option value="food_hospitality">Food &amp; Hospitality</option>
          <option value="wellness_fitness">Wellness &amp; Fitness</option>
          <option value="insurance">Insurance</option>
          <option value="retail">Retail</option>
          <option value="other">Other</option>
        </select>
      </div>

      {error && (
        <p role="alert" style={{ color: 'rgb(252,165,165)', fontSize: 13, margin: 0 }}>
          {error}
        </p>
      )}

      <V4CtaButton type="submit" size="lg">
        {status === 'submitting' ? 'Sending…' : 'Request walkthrough'}
      </V4CtaButton>

      <p
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--dimmer)',
          margin: 0,
        }}
      >
        30-day guarantee. We&apos;ll follow up within one business day.
      </p>
    </form>
  );
}
